import { type UnifiedTransfer } from '@column/api';

import {
  formatTransferType,
  getBatchNetAmount,
  isBatchAmountUnavailable,
  isBatchTransfer,
} from '~/lib/transfers/utils';
import { type TransferEntryType, convertTransferAmount } from '~/repositories';

export interface ReviewCurrencyTotal {
  currencyCode: string;
  amount: number;
}

export interface ReviewDirectionTotals {
  debits: ReviewCurrencyTotal[];
  credits: ReviewCurrencyTotal[];
}

export interface ReviewTypeCount {
  type: string;
  label: string;
  count: number;
}

const toCurrencyTotals = (totals: Map<string, number>): ReviewCurrencyTotal[] =>
  [...totals].filter(([, amount]) => amount > 0).map(([currencyCode, amount]) => ({ currencyCode, amount }));

const isMoneyIn = (transfer: Pick<UnifiedTransfer, 'amount' | 'is_incoming' | 'type'>): boolean =>
  isBatchTransfer(transfer)
    ? getBatchNetAmount(transfer) > 0
    : convertTransferAmount(transfer.type as TransferEntryType, transfer.is_incoming) !== -1;

export const getReviewTotals = (
  transfers: Pick<UnifiedTransfer, 'amount' | 'currency_code' | 'is_incoming' | 'status' | 'type'>[]
): ReviewDirectionTotals | undefined => {
  if (transfers.some(isBatchAmountUnavailable)) return undefined;
  const debits = new Map<string, number>();
  const credits = new Map<string, number>();
  transfers.forEach((transfer) => {
    const totals = isMoneyIn(transfer) ? credits : debits;
    totals.set(transfer.currency_code, (totals.get(transfer.currency_code) ?? 0) + Math.abs(transfer.amount));
  });
  return { debits: toCurrencyTotals(debits), credits: toCurrencyTotals(credits) };
};

export const getReviewTypeCounts = (transfers: Pick<UnifiedTransfer, 'type'>[]): ReviewTypeCount[] => {
  const counts = new Map<string, ReviewTypeCount>();
  transfers.forEach(({ type }) => {
    const label = formatTransferType(type as TransferEntryType, { abbreviated: false });
    const existing = counts.get(label);
    counts.set(label, { type: existing?.type ?? type, label, count: (existing?.count ?? 0) + 1 });
  });
  return [...counts.values()];
};
